import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from './Types/User.type';
import { Commerce } from './Types/Commerce.type';
import { auth, db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { CollectionEnum } from './Types/Common.type';
import { onAuthStateChanged } from 'firebase/auth';

type AppContextType = {
  user: User | null;
  setUser: (user: User | null) => void;
  commerce: Commerce | null;
  setCommerce: (commerce: Commerce | null) => void;
  isLoading: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [commerce, setCommerce] = useState<Commerce | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userDocRef = doc(db, CollectionEnum.USERS, firebaseUser.uid);
          const userSnap = await getDoc(userDocRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data() as User;
            setUser(userData);
            
            // Get commerce data
            const commerceId = userData.commerce_id;
            const commerceDocRef = doc(db, CollectionEnum.COMMERCES, commerceId);
            const commerceSnap = await getDoc(commerceDocRef);
            
            if (commerceSnap.exists()) {
              const commerceData = commerceSnap.data() as Commerce;
              setCommerce(commerceData);
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        setUser(null);
        setCommerce(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AppContext.Provider value={{ user, setUser, commerce, setCommerce, isLoading }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};
